/* @flow */
declare var __CLIENT__: boolean;
declare var __CAPACITOR__: boolean;

// Imports
import AppChildrenOrError from '../components/common/AppChildrenOrError';
import * as React from 'react';
import type { Connector } from 'react-redux'; // eslint-disable-line
import { connect } from 'react-redux';
import { compose } from 'redux';
import classnames from 'classnames';
import { last } from 'lodash';
import ReactModal from 'react-modal';

import AppHeader from '../components/chrome/appheader/';
import PlayerBar from '../components/player/PlayerBar';
import Notifications from '../components/notifications/Notifications';
import Sidebar from '../components/common/Sidebar';
import OfflineScreen from '../components/capacitor/OfflineScreen';

import * as clientActions from '../actions/client';
import { selectModalOpen } from '../selectors/modals';
import {
  selectIsGeoBlockedCountry,
  selectIsInApp,
  selectIsConnected,
  selectWindowInsets,
  selectHelmet,
} from '../selectors/client';
import { selectFeatureFlags } from '../selectors/features';
import { selectIsOnLandingPage } from '../selectors/routing';
import ModalContainer from '../components/modals/ModalContainer';
import { setViewportHeight, setWindowInsets } from '../utils/dom';
import { CHROMES_HIDE_SIDEBAR } from '../constants';
import { FeatureContext } from '../components/util/Feature';
import { EventChannelPlugin } from '../capacitor-connector';

import type { ThunkAction } from '../actions/types';
import type { FeatureFlags } from '../reducers/client/features';
import Head from '../components/chrome/Head';

type OwnProps = {
  children: React.ChildrenArray<React.Element<any>>,
  routes: [{ hideUi: boolean }],
};

type MapStateToProps = {
  chrome: string,
  playerbarVisible: boolean,
  modalOpen: boolean,
  isGeoBlockedCountry: boolean,
  featureFlags: FeatureFlags,
  isInApp: boolean,
  children: React.ChildrenArray<React.Element<any>>,
  hideUi: boolean,
  isConnected: boolean,
  windowInsets: { top: number, bottom: number, left: number, right: number },
  helmet: Object,
};

type DispatchProps = {
  onKeyDown: KeyboardEventHandler,
  loadImageAnnotations: () => ThunkAction,
};

type Props = MapStateToProps & DispatchProps;

class App extends React.PureComponent<Props> {
  componentWillMount() {
    if (__CLIENT__) {
      setViewportHeight();

      if (this.props.windowInsets) {
        setWindowInsets(this.props.windowInsets);
      }

      ReactModal.setAppElement(document.getElementById('root'));
    }
  }

  componentDidMount() {
    const { onKeyDown } = this.props;
    document.addEventListener('keydown', onKeyDown);

    if (!this.props.isGeoBlockedCountry) {
      this.props.loadImageAnnotations();
    }

    if (__CAPACITOR__) {
      EventChannelPlugin.webAppSendsIsMounted();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.windowInsets !== prevProps.windowInsets) {
      setWindowInsets(this.props.windowInsets);
    }
  }

  render() {
    const { playerbarVisible, modalOpen, chrome, children, isInApp, isConnected, hideUi } =
      this.props;

    const chromeHidesSidebar = CHROMES_HIDE_SIDEBAR.includes(chrome);
    const showSidebar = !chromeHidesSidebar && !isInApp && !hideUi;
    const showPlayerbar = playerbarVisible && !isInApp;

    const className = classnames(`app-chrome-${chrome}`, 'is-logged-in', {
      'playerbar-visible': showPlayerbar,
      'sidebar-visible': showSidebar,
      'is-in-app': isInApp,
      'is-capacitor': !!__CAPACITOR__,
    });

    if (!isConnected && __CAPACITOR__) {
      return (
        <section className="content" id="app-content">
          <OfflineScreen />
          <Notifications />
        </section>
      );
    }

    const appContent = (
      <div className={className}>
        <Head {...this.props.helmet} />
        {!isInApp && !hideUi && <AppHeader />}
        {showSidebar && <Sidebar />}
        <section className="content" id="app-content" data-test="app-content">
          <AppChildrenOrError>{children}</AppChildrenOrError>
          <div className="bottom-spacer" aria-hidden="true" />
        </section>
        {!modalOpen && <Notifications />}
        {!isInApp && !hideUi && <PlayerBar />}
        <ModalContainer />
        <ec-sidebar-wrapper custom-landing-page salesChannel="idagio" is-in-app={isInApp} />
      </div>
    );

    return (
      <FeatureContext.Provider value={this.props.featureFlags}>
        {appContent}
      </FeatureContext.Provider>
    );
  }
}

function mapStateToProps(state: Object, ownProps: OwnProps): MapStateToProps {
  // TODO properly type state
  const clientUi = state.client.ui;

  return {
    modalOpen: selectModalOpen(state),
    chrome: clientUi.chrome,
    playerbarVisible: !clientUi.playerbarHidden,
    shortcutsHelpIsVisible: clientUi.shortcutsHelpIsVisible,
    children: ownProps.children,
    isGeoBlockedCountry: selectIsGeoBlockedCountry(state),
    isLandingPage: selectIsOnLandingPage(state),
    featureFlags: selectFeatureFlags(state),
    isInApp: selectIsInApp(state),
    isConnected: selectIsConnected(state),
    hideUi: last(ownProps.routes).hideUi,
    windowInsets: selectWindowInsets(state),
    helmet: selectHelmet(state),
  };
}

const dispatchProps: DispatchProps = {
  onKeyDown: clientActions.onKeyDown,
  loadImageAnnotations: clientActions.loadImageAnnotations,
};

const connector: Connector<OwnProps, Props> = connect(mapStateToProps, dispatchProps);

export default compose(connector)(App);

export { App as PureApp };
